/**
 * StayOn-Button — Baukasten: Knöpfe sind Pillen.
 *
 * Varianten (Namen bleiben für bestehende Konsumenten):
 *   - brand:       Wald hell — Hauptaktion, höchstens eine je Bereich (Speichern, Anlegen)
 *   - primary:     Tinte — Arbeitsschritt (Mapping öffnen, Weiter)
 *   - secondary:   Nebenfläche — Zweitaktion (Abbrechen in Dialogen, Filter)
 *   - ghost:       leise — Nebenaktion ohne Fläche
 *   - ghost-dark:  leise auf dunklen Flächen
 *   - link:        Text mit Unterstreichung beim Überfahren — Inline-Aktionen
 *   - destructive: Siegellack — Unumkehrbares (Stornieren, Löschen), immer mit Bestätigung
 *
 * Keine Hover-Lift-Motion — Mockups zeigen ruhige Buttons ohne Bewegung.
 */
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/shared/lib/cn';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2',
    'rounded-pill font-sans font-semibold',
    'transition-colors duration-fast ease-brand',
    'whitespace-nowrap select-none',
    'disabled:opacity-40 disabled:pointer-events-none',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
    'focus-visible:outline-forest',
  ],
  {
    variants: {
      variant: {
        brand: 'bg-forest text-white hover:bg-forest-deep',
        primary: 'bg-ink text-white hover:bg-ink/85',
        secondary: 'bg-card-alt text-ink hover:bg-hairline',
        ghost: 'bg-transparent text-ink hover:bg-card-alt',
        'ghost-dark': 'bg-transparent text-paper hover:bg-paper/10',
        link: 'bg-transparent text-ink hover:text-forest underline-offset-4 hover:underline p-0 rounded-none h-auto',
        destructive: 'bg-sealwax text-white hover:bg-sealwax/90',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'px-[17px] py-[10px] text-[13px]',
        lg: 'h-12 px-5 text-sm',
        icon: 'h-10 w-10 p-0',
      },
    },
    defaultVariants: {
      variant: 'ghost',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      disabled,
      leftIcon,
      rightIcon,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const isDisabled = disabled || loading;

    // Slot erlaubt nur EIN Kind-Element und merged Props darauf. Bei asChild=true
    // gehen leftIcon/rightIcon/loading-Slots deshalb an den Consumer: der packt
    // die Icons direkt in sein Child-Element (z.B. <Link>...icons + label</Link>).
    if (asChild) {
      return (
        <Comp className={cn(buttonVariants({ variant, size }), className)} ref={ref} {...props}>
          {children}
        </Comp>
      );
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {loading ? <Loader2 className="size-4 animate-spin" strokeWidth={2} /> : leftIcon}
        {children}
        {!loading && rightIcon}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
